import { getCurrentSystem, isLocalDevEnv } from '@umu/ukit-lang';
import { $window, addQueryString } from '@umu/umu-dom';
import { safeJSONParse } from '../../@utils/jsonParse';
import get from 'lodash/get';
import isError from 'lodash/isError';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import dayjs from 'dayjs';
import { reportError } from 'common/src/services/errorLogger';
interface LogParams {
    tag: string;
    event: string;
    data: string;
    /** umuId */
    uid: string;
    /** enterpriseId */
    eid: string;
    /** 网络状态 */
    net: string;
    /** 触发时间 */
    t: number;
    /** 事件窗口内的触发次数 */
    c?: number;
}

/** 将对象中的value为json str的解出来 */
const formatLog = (data: LogParams) => {
    const result: any = {};
    for (const key in data) {
        const value = data[key as keyof LogParams];
        if (typeof value === 'string') {
            result[key] = safeJSONParse(value as string, value);
        } else {
            result[key] = value;
        }
    }
    return result;
};

const formatData = (data: any): any => {
    if (isError(data)) {
        return String(data);
    }
    if (isObject(data)) {
        const newData: Record<string, any> = { ...data };
        for (const key in newData) {
            if (isError(newData[key])) {
                newData[key] = String(newData[key]);
            }
        }
        return JSON.stringify(newData);
    }
    return data ?? undefined;
};

const MAX_NO_NETWORK_QUEUE = 50;

interface ActionLoggerOptions {
    tag?: string;
    timeWindow?: number;
    debug?: boolean;
    logWithDev?: boolean;
}
class ActionLogger {
    private noNetworkQueue: any = [];
    private networkType: {
        isConnected: boolean;
        type: string;
    } | null = null;
    private tag = 'umu_web';
    private eventCounter: { [key: string]: { count: number; ft: number; lt: number; timeoutId?: number } } = {};
    private timeWindow = 5000; // 5秒
    private debug = false;
    private logWithDev = false;
    /**
     * 创建一个新的 ActionLogger 实例
     * @param options - 配置选项
     * @param options.tag - 事件标签,用于追踪一系列事件
     * @param options.timeWindow - 事件时间窗口,单位毫秒。在该时间窗口内的事件会被合并为一次上报。第一次事件会立即上报
     * @param options.debug - 是否开启调试模式，调试模式下会在控制台打印log
     * @description
     * 1. new ActionLogger() 会自动初始化，上报初始化日志
     */
    constructor(options: ActionLoggerOptions = {}) {
        this.tag = options.tag || this.tag;
        this.timeWindow = options.timeWindow ?? this.timeWindow;
        this.debug = options.debug || this.debug;
        this.logWithDev = options.logWithDev || this.logWithDev;
        this.subscribeNetworkStatus();
        window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
        this.initLog();
    }

    public initLog() {
        const pageConfig = get($window, 'pageData.config', {});

        const params = {
            ...omit(pageConfig, [
                'fixed',
                'i18n_config',
                'systems',
                'page_token',
                'theme_config',
                'pay_channel',
                'step_length',
            ]),
        };
        const userInfo = this.getUserInfo();
        if (userInfo.umuId) {
            this.log('init_log', params);
        } else {
            console.warn('action log init user info error; tag: ' + this.tag);
        }
    }
    private async subscribeNetworkStatus() {
        this.updateNetworkType();

        window.addEventListener('online', this.handleNetworkChange.bind(this));
        window.addEventListener('offline', this.handleNetworkChange.bind(this));

        if ('connection' in navigator) {
            const connection = (navigator as any).connection;
            connection.addEventListener('change', this.updateNetworkType.bind(this));
        }
    }

    private updateNetworkType() {
        if ('connection' in navigator) {
            const connection = (navigator as any).connection;
            this.networkType = {
                isConnected: navigator.onLine,
                type: connection.effectiveType || '',
            };
        } else {
            this.networkType = {
                isConnected: navigator.onLine,
                type: '',
            };
        }
    }

    private async handleNetworkChange() {
        this.updateNetworkType();
        if (this.networkType?.isConnected) {
            while (this.noNetworkQueue.length > 0) {
                const params = this.noNetworkQueue.shift();
                try {
                    await this.send(params);
                } catch (error) {
                    this.noNetworkQueue.unshift(params);
                    break;
                }
            }
        }
    }

    private enrichParams(event: string, data: string): LogParams {
        const userInfo = this.getUserInfo();
        return {
            uid: userInfo.umuId || '',
            eid: userInfo.enterpriseId || '',
            net: JSON.stringify({
                c: this.networkType?.isConnected ? 1 : 0,
                t: this.networkType?.type,
            }),
            tag: this.tag,
            t: +new Date(),
            event,
            data,
        };
    }

    private getUserInfo() {
        const userInfo = get(window, 'pageData.data.user_info', {});
        return {
            umuId: userInfo.umu_id || '',
            enterpriseId: userInfo.enterprise_id || '',
        };
    }

    private getLogUrl() {
        const lang = getCurrentSystem();
        const urlMap: { [key: string]: string } = {
            EN: 'https://log.umu.com/hm2.gif',
            EM: 'https://log.umu.io/hm2.gif',
            TW: 'https://log.umu.tw/hm2.gif',
            JP: 'https://log.umu.co/hm2.gif',
            CN: 'https://log.umu.cn/hm2.gif',
            default: 'https://log.umu.com/hm2.gif',
        };
        if (isLocalDevEnv) {
            return `https://log.${window.pageData?.config?.site_host || 'dev16.umu.com'}/hm2.gif`;
        }
        return urlMap[lang] || urlMap['default'];
    }

    private async send(params: LogParams) {
        // dev环境的logUrl 始终为dev16,可手动修改
        const url = this.getLogUrl();
        const logUrl = addQueryString(url, params);

        if (isLocalDevEnv && !this.logWithDev) {
            // 测试环境不发送log
            return;
        }

        if (isLocalDevEnv && this.debug) {
            const title = [
                `%c 🚀 action logger 🚀: ${params.event}`,
                'color: #0AC160; font-weight: bold;',
                dayjs().format('hh:mm:ss'),
            ];
            console.groupCollapsed(...title);
            params && console.log(formatLog(params));
            console.groupEnd();
        }

        if (navigator.sendBeacon) {
            const inQueue = navigator.sendBeacon(logUrl);
            if (!inQueue) {
                this.noNetworkQueue.push(params);
            }
        } else {
            const img = new Image();
            img.src = logUrl;
            img.onerror = () => {
                this.noNetworkQueue.push(params);
            };
        }
    }

    private handleBeforeUnload() {
        // 页面卸载时，使用 sendBeacon 同步发送所有待发送的日志
        // 注意：不能使用异步的 send 方法，因为浏览器可能在异步操作完成前就关闭页面
        this.noNetworkQueue.forEach((params: LogParams) => {
            const url = this.getLogUrl();
            const logUrl = addQueryString(url, params);
            if (navigator.sendBeacon) {
                navigator.sendBeacon(logUrl);
            }
        });
        // 清空队列
        this.noNetworkQueue = [];
    }
    private handleEventCount(params: LogParams) {
        const eventKey = `${this.tag}_${params.event}`;
        const triggerTime = params.t;

        // 第一次直接发送
        if (!this.eventCounter[eventKey]) {
            this.eventCounter[eventKey] = { count: 0, ft: triggerTime, lt: triggerTime };
        }

        const eventInfo = this.eventCounter[eventKey];

        // +500ms是为了防止时间戳不准确
        const isEventOutdated = triggerTime - eventInfo.ft > this.timeWindow + 500;

        if (isEventOutdated) {
            // 如果时间窗口已过，清除旧的定时器并重置计数器
            if (eventInfo.timeoutId) {
                clearTimeout(eventInfo.timeoutId);
            }
            eventInfo.count = 0;
            eventInfo.ft = triggerTime;
            eventInfo.timeoutId = undefined;
        }

        eventInfo.count++;
        eventInfo.lt = triggerTime;

        if (eventInfo.count === 1) {
            // 时间窗口内的第一个事件，立即发送
            this.send({ ...params, c: eventInfo.count });
        }

        // 设置一个定时器，在时间窗口结束时重新上报剩余次数
        if (eventInfo.timeoutId) {
            return;
        }
        eventInfo.timeoutId = window.setTimeout(() => {
            if (eventInfo.count > 1) {
                this.send({ ...params, c: eventInfo.count });
            }
            this.eventCounter[eventKey] = {
                count: 0,
                ft: +new Date(),
                lt: +new Date(),
                timeoutId: undefined,
            };
        }, this.timeWindow);
    }

    /**
     *
     * @param event 事件名
     * @param params 事件参数,可以直接将catch中的error传入，做了对应处理；
     * @example
     * ```ts
     * log('click', { name: 'test' })
     * log('click', new Error('test'))
     * log('click', 'test')
     * log('click')
     * log('click', {err: new Error('test')}) //这样的嵌套可以接受，但是 Error不能嵌套更深了，否则会被JSON.stringify成{},需要手动处理
     * 不建议嵌套太深
     * ```
     */
    public async log(event: string, params?: any) {
        try {
            const data = formatData(params);
            const newParams = this.enrichParams(event, data);

            if (!newParams.uid) {
                return;
            }
            if (!this.networkType?.isConnected) {
                if (this.noNetworkQueue.length > MAX_NO_NETWORK_QUEUE) {
                    // 替换最早的
                    this.noNetworkQueue.shift();
                }
                this.noNetworkQueue.push(newParams);
                return;
            }

            // timeWindow 为 0 时，每次都立即发送，不走合并逻辑
            if (this.timeWindow === 0) {
                this.send(newParams);
            } else {
                this.handleEventCount(newParams);
            }
        } catch (e) {
            reportError({
                error: e,
                componentName: 'ActionLogger',
                additionalInfo: { desc: 'log error', event, params },
            });
        }
    }
}

export { ActionLogger };
